import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    TextField,
    Container,
    RadioGroup,
    FormControlLabel,
    Radio,
    Rating,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import ApiButton from '../../UIControls/api-button/api-button';
import { useSendSurvey } from './use-send-survey';

const SurveyScreen = () => {
    const navigate = useNavigate();
    const sendSurvey = useSendSurvey();

    // Form state
    const [rating, setRating] = useState(0);
    const [valueForMoney, setValueForMoney] = useState('');
    const [willingToPay, setWillingToPay] = useState('');
    const [recommend, setRecommend] = useState('');
    const [futureSuggestions, setFutureSuggestions] = useState('');
    const [additionalComments, setAdditionalComments] = useState('');
    const [chatConsent, setChatConsent] = useState('');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleBack = () => {
        navigate(-1);
    };

    const submitSurvey = async () => {
        const payload = {
            rating,
            valueForMoney,
            willingToPay: valueForMoney === 'Too Expensive' ? willingToPay : null,
            recommend,
            futureSuggestions,
            additionalComments,
            chatConsent,
        };
        console.warn('Submitting survey data:', payload);
        await sendSurvey(payload);
    };

    const resetAndNavigateBack = () => {
        setRating(0);
        setValueForMoney('');
        setWillingToPay('');
        setRecommend('');
        setFutureSuggestions('');
        setAdditionalComments('');
        setChatConsent('');
        navigate(-1);
    };

    return (
        <Container maxWidth="sm" sx={{ width: '100%', backgroundColor: 'white', p: 4, my: 8 }}>
            <Box display="flex" alignItems="center">
                <IconButton onClick={handleBack} aria-label="Go back">
                    <ArrowBackIcon />
                </IconButton>
            </Box>

            <Typography variant="h5" mt={2} mb={2}>
                We appreciate your input!
            </Typography>
            <Typography variant="body1" mb={4}>
                To help us enhance our service, we kindly ask you to take a moment to complete our short survey. Your
                feedback is greatly appreciated!
            </Typography>

            {/* Rating */}
            <Typography variant="h6" mb={1}>
                What would you rate Cetra out of 5? *
            </Typography>
            <Rating value={rating} onChange={(e, newValue) => setRating(newValue)} max={5} sx={{ mb: 2 }} />

            {/* Value for Money */}
            <Typography variant="h6" mb={1}>
                Do you think Cetra is good value for money? *
            </Typography>
            <RadioGroup value={valueForMoney} onChange={(e) => setValueForMoney(e.target.value)} sx={{ mb: 2 }}>
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
                <FormControlLabel value="Too Expensive" control={<Radio />} label="It's too expensive" />
            </RadioGroup>

            {/* Conditional Willing to Pay Field */}
            {valueForMoney === 'Too Expensive' && (
                <Box mb={2}>
                    <Typography variant="h6" mb={1}>
                        What would you be willing to pay per month?
                    </Typography>
                    <TextField
                        value={willingToPay}
                        onChange={(e) => setWillingToPay(e.target.value)}
                        fullWidth
                        placeholder="In GBP"
                        multiline
                        rows={2}
                    />
                </Box>
            )}

            {/* Recommendation */}
            <Typography variant="h6" mb={1}>
                Would you recommend Cetra to a friend/colleague? *
            </Typography>
            <RadioGroup value={recommend} onChange={(e) => setRecommend(e.target.value)} sx={{ mb: 2 }}>
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
            </RadioGroup>

            {/* Future Suggestions */}
            <TextField
                label="What would you like to see on Cetra in the future?"
                value={futureSuggestions}
                onChange={(e) => setFutureSuggestions(e.target.value)}
                fullWidth
                multiline
                rows={4}
                sx={{ mb: 2 }}
            />

            {/* Additional Comments */}
            <TextField
                label="Additional Comments"
                value={additionalComments}
                onChange={(e) => setAdditionalComments(e.target.value)}
                fullWidth
                multiline
                rows={4}
                sx={{ mb: 2 }}
            />

            {/* Chat Consent */}
            <Typography variant="h6" mb={1}>
                Would you like to chat with a member of our team to share your thoughts/ideas further?
            </Typography>
            <RadioGroup value={chatConsent} onChange={(e) => setChatConsent(e.target.value)} sx={{ mb: 2 }}>
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
            </RadioGroup>

            {/* Submit Button */}
            <ApiButton
                apiCall={submitSurvey}
                buttonLabel="Submit"
                fullWidth={true}
                color="success"
                errorTitle="Survey Error"
                errorMessage="There was a problem submitting your survey. Please try again."
                successTitle="Survey Submitted"
                successMessage="Your survey has been submitted successfully."
                disabled={
                    !rating || !valueForMoney || !recommend || (valueForMoney === 'Too Expensive' && !willingToPay)
                }
                onSuccessConfirm={resetAndNavigateBack}
            />
        </Container>
    );
};

export default SurveyScreen;
